import { Grid } from "@mui/material";
import { Common } from "../../../common/src";
import { ClusteringItems } from "./ClusteringItems";
import { Dispatch, SetStateAction, useMemo } from "react";
import { EViewType } from "../../pages/Home";
import Clusterings from "../Utility/Clusterings";
import { ColumnVisibility } from "./parts/projectListColumnWidths";

interface Props {
  displayProjects: { [name: string]: [Common.Project] };
  selectProject: Common.Project[];
  setSelectProject: (project: Common.Project[]) => void;
  isLanguage: boolean;
  jobsiteList: Common.LandlogJobSite[];
  userList: Common.ProjectUserList[];
  setErrorSnackbar: Dispatch<SetStateAction<boolean>>;
  setErrorMsg: Dispatch<SetStateAction<string>>;
  setOpenWarning: Dispatch<SetStateAction<boolean>>;
  setMsgWarning: Dispatch<SetStateAction<string>>;
  isARAvailable: boolean;
  viewType: EViewType;
  sortBy?: string;
  visibleColumns: ColumnVisibility;
}

// Sort
const displaySortCompare = (aStr: string, bStr: string): number => {
  if (aStr < bStr) return 1;
  if (aStr > bStr) return -1;
  else return 0;
};

// localeCompare(str, undefined, options)は比較ごとにCollatorを生成するため、
// 一度だけ生成して再利用する
const nameCollator = new Intl.Collator(undefined, {
  numeric: true,
  sensitivity: "base",
});

const ProjectList = (props: Props) => {
  // Memoize sorting configuration để tránh tạo lại mỗi render
  const sortConfig = useMemo(
    () => ({
      // 正規表現 日本語(/^[ぁ-んァ-ヶｱ-ﾝﾞﾟ一-龠]*$/)
      regexNumber: /[0-9]/,
      regexEnglish: /[a-zA-Z]/,
      regexKanzi: /^[一-龠]*$/,
      regexKatakana: /^[ァ-ヶｱ-ﾝﾞﾟ]*$/,
      regexHiragana: /^[ぁ-ん]*$/,
    }),
    [],
  );

  // Cache sort order và getSortValue function
  const { getSortValue } = useMemo(() => {
    // ソート順「ひらがな、カタカナ、漢字、英語、数字」
    const sortOrder = [
      sortConfig.regexNumber,
      sortConfig.regexEnglish,
      sortConfig.regexKanzi,
      sortConfig.regexKatakana,
      sortConfig.regexHiragana,
    ];
    return {
      getSortValue: (sortStr: string) => {
        return sortOrder.findIndex((pattern) => pattern.test(sortStr));
      },
    };
  }, [sortConfig]);

  // Memoize sorted projects để tránh re-sort khi chỉ viewType thay đổi.
  // 比較キー(日付文字列・名前種別)は比較関数の中で毎回計算するとO(n log n)回
  // 評価されるため、事前に1回だけ計算してからソートする
  const sortedProjects = useMemo(() => {
    const result = { ...props.displayProjects };

    Object.keys(result).forEach((key) => {
      const decorated = result[key].map((project) => ({
        project,
        dateStr:
          Clusterings.resolveSortDate(project, props.sortBy)?.toString() ?? "",
        nameLevel: getSortValue(project.name.charAt(0)),
      }));

      decorated.sort((a, b) => {
        if (a.dateStr !== b.dateStr) {
          return displaySortCompare(a.dateStr, b.dateStr);
        }
        if (a.nameLevel === b.nameLevel) {
          return nameCollator.compare(a.project.name, b.project.name);
        }
        return b.nameLevel - a.nameLevel;
      });

      // Tạo array mới để tránh mutate original array
      result[key] = decorated.map((item) => item.project) as [Common.Project];
    });

    return result;
  }, [props.displayProjects, props.sortBy, getSortValue]);

  // Memoize ascKeys để tránh re-sort keys
  const ascKeys = useMemo(() => {
    return Object.keys(sortedProjects).sort((a, b) => {
      return displaySortCompare(a, b);
    });
  }, [sortedProjects]);

  // Memoize clusteringItems để chỉ re-render khi cần thiết
  const clusteringItems = useMemo(() => {
    return ascKeys.map((key) => {
      return (
        <ClusteringItems
          displayProjects={sortedProjects}
          index={key}
          selectProject={props.selectProject}
          setSelectProject={props.setSelectProject}
          key={key}
          isLanguage={props.isLanguage}
          userList={props.userList}
          jobsiteList={props.jobsiteList}
          setErrorSnackbar={props.setErrorSnackbar}
          setErrorMsg={props.setErrorMsg}
          setOpenWarning={props.setOpenWarning}
          setMsgWarning={props.setMsgWarning}
          isARAvailable={props.isARAvailable}
          viewType={props.viewType}
          sortBy={props.sortBy}
          visibleColumns={props.visibleColumns}
        />
      );
    });
  }, [
    ascKeys,
    sortedProjects,
    props.selectProject,
    props.setSelectProject,
    props.isLanguage,
    props.userList,
    props.jobsiteList,
    props.setErrorSnackbar,
    props.setErrorMsg,
    props.setOpenWarning,
    props.setMsgWarning,
    props.isARAvailable,
    props.viewType,
    props.sortBy,
    props.visibleColumns,
  ]);
  //リストを整形して返却
  return (
    <div
      // ref={containerRef}
      style={{
        height: "100%",
        width: "100%",
        overflowY: "auto",
      }}
    >
      <Grid
        container
        sx={{
          padding:
            props.viewType === EViewType.LIST ? "0 4px 32px 4px" : "0px 16px",
          width: "100%",
        }}
      >
        {clusteringItems}
      </Grid>
    </div>
  );
};

export default ProjectList;
